import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  CssBaseline,
  styled,
  Theme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import MenuIcon from '@mui/icons-material/Menu';
import Sidebar from './Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { THEME_MODES, STORAGE_KEYS, APP_NAME, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { getMyCompany, getMySubscription } from '../services/companyService';
import { getMySubscriptionData, setMySubscriptionData, type MySubscriptionStoredData } from '../helpers/storageUtils';
import SubscriptionCancellationBanner from './SubscriptionCancellationBanner';

const drawerWidth = 240;
const closedDrawerWidth = 60; // Must match Sidebar's closed width
/** Height of the subscription cancellation banner (fixed at top); used for AppBar top offset and main pt */
const SUBSCRIPTION_BANNER_HEIGHT = 48;

 // Styled AppBar for smooth transition when sidebar opens/closes
 const AppBarStyled = styled(AppBar, {
   shouldForwardProp: (prop: string) => prop !== 'open' && prop !== 'headerOnly',
 })<{ open?: boolean; headerOnly?: boolean }>(({ theme, open, headerOnly }: { theme: Theme; open?: boolean; headerOnly?: boolean }) => ({
   zIndex: theme.zIndex.drawer + 1,
   transition: theme.transitions.create(['width', 'margin'], {
     easing: theme.transitions.easing.sharp,
     duration: theme.transitions.duration.leavingScreen,
   }),
   ...(headerOnly && {
     marginLeft: 0,
     width: '100%',
   }),
   ...(!headerOnly && {
     marginLeft: open ? `${drawerWidth}px` : `${closedDrawerWidth}px`,
     width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${closedDrawerWidth}px)`,
   }),
 }));

const Layout: React.FC<{ children: React.ReactNode; headerOnly?: boolean }> = ({ children, headerOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<MySubscriptionStoredData | null>(() => getMySubscriptionData());
  const { themeMode, setThemeMode, isDarkMode } = useTheme();
  const { sessionExpired, isAuthenticated } = useAuth();
  const location = useLocation();

  // Check if current route is threads page (with or without channel ID)
  const isThreadsPage = location.pathname.startsWith('/threads');

  // Fetch company name from API if not in localStorage
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (!storedUserData) {
          return;
        }

        // Check if we have authentication tokens before making API call
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        const authToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!refreshToken && !authToken) {
          // No tokens available, skip API call
          return;
        }

        const userData = JSON.parse(storedUserData);
        
        // First check if company_name already exists in localStorage
        const existingCompanyName = userData.company_name || userData.companyName;
        if (existingCompanyName) {
          setCompanyName(existingCompanyName);
          return;
        }

        // If company name not found, fetch it from API using /my endpoint
        try {
          const companyResponse = await getMyCompany();

          // Extract company name from response (handle different response formats)
          const fetchedCompanyName = companyResponse.name || companyResponse.data?.name || null;
          
          if (fetchedCompanyName) {
            // Update localStorage with company name
            const updatedUserData = {
              ...userData,
              company_name: fetchedCompanyName,
              companyName: fetchedCompanyName
            };
            localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(updatedUserData));
            setCompanyName(fetchedCompanyName);
          }
        } catch (apiError: any) {
          // Only log error if it's not a 401 (unauthorized) - that's expected if user is not logged in
          if (apiError?.message && !apiError.message.includes('401') && !apiError.message.includes('Authentication required')) {
            console.error('Error fetching company name from API:', apiError);
          }
          // Don't set company name if API call fails
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    };

    // Fetch when on Admin Hub or supportSpace so company name is in localStorage for plan upgrade and UI
    if (location.pathname === '/adminHub' || location.pathname === '/supportSpace') {
      fetchCompanyName();
    }
  }, [location.pathname]);

  // Fetch mySubscription when authenticated and subscription enabled (for cancellation banner)
  useEffect(() => {
    if (DISABLE_SUBSCRIPTION || !isAuthenticated || headerOnly) return;
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return;
    getMySubscription(token)
      .then((res) => {
        if (res?.success && res?.data) {
          const data = res.data as unknown as MySubscriptionStoredData;
          setMySubscriptionData(data);
          setSubscriptionData(data);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, headerOnly]);

  // When subscription is updated elsewhere (e.g. after cancel on Onboarding), sync state so banner shows immediately
  useEffect(() => {
    const handleSubscriptionUpdated = () => {
      setSubscriptionData(getMySubscriptionData());
    };
    window.addEventListener('subscription-updated', handleSubscriptionUpdated);
    return () => window.removeEventListener('subscription-updated', handleSubscriptionUpdated);
  }, []);

  // Function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/' || path === '/supportSpace') {
      return LanguageConstants.topic_s;
    } else if (path === '/adminHub') {
      return companyName ? `Admin Hub - ${companyName}` : 'Admin Hub';
    } else if (path === '/threads') {
      return `${LanguageConstants.thread} Detail`;
    } else if (path === '/profile') {
      return 'Profile Settings';
    } else if (path === '/settings') {
      return 'Settings';
    } else if (path === '/approvals') {
      return 'Approvals';
    } else if (path === '/tasks') {
      return 'Tasks';
    } else if (path === '/onboarding') {
      return 'Company Onboarding';
    }
    return APP_NAME || 'Prism 7';
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleThemeChange = (mode: 'system' | 'light' | 'dark') => {
    setThemeMode(mode);
    handleThemeMenuClose();
  };

  // Add class to body based on sidebar state for CSS-based adjustments
  useEffect(() => {
    if (headerOnly) return;
    
    if (open) {
      document.body.classList.add('sidebar-open');
      document.body.classList.remove('sidebar-closed');
    } else {
      document.body.classList.add('sidebar-closed');
      document.body.classList.remove('sidebar-open');
    }
    
    return () => {
      document.body.classList.remove('sidebar-open', 'sidebar-closed');
    };
  }, [open, headerOnly]);

  // When any API returns 401, redirect to centralized sessionExpired page (after all hooks to avoid "fewer hooks" error)
  if (sessionExpired && location.pathname !== '/sessionExpired') {
    return <Navigate to="/sessionExpired" replace />;
  }

  // After re-login, do not keep showing the session-expired page on /sessionExpired
  if (isAuthenticated && !sessionExpired && location.pathname === '/sessionExpired') {
    return <Navigate to="/supportSpace" replace />;
  }

  // Hide banner on onboarding: plan step already shows reactivate on the Starter card
  const showSubscriptionBanner = Boolean(
    !DISABLE_SUBSCRIPTION &&
    location.pathname !== '/onboarding' &&
    subscriptionData?.subscriptionStatus?.toLowerCase() === 'inactive' &&
    subscriptionData?.endDate
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* Subscription cancellation banner fixed at very top, above the AppBar; left offset so it does not overlap sidebar */}
      {showSubscriptionBanner && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: headerOnly ? 0 : open ? drawerWidth : closedDrawerWidth,
            right: 0,
            // Threads page: use z-index 1 so banner sits below toaster/overlays; other pages: above drawer/AppBar
            zIndex: isThreadsPage ? 1 : (theme) => theme.zIndex.drawer + 2,
            transition: (theme) =>
              theme.transitions.create('left', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          }}
        >
          <SubscriptionCancellationBanner subscriptionData={subscriptionData} />
        </Box>
      )}
             {/* Conditionally render AppBar - hide for threads page */}
       {!isThreadsPage && (
         <AppBarStyled
           position="fixed"
           open={headerOnly ? false : open}
           headerOnly={headerOnly}
           sx={{
             top: showSubscriptionBanner ? SUBSCRIPTION_BANNER_HEIGHT : 0,
             backgroundColor: isDarkMode ? 'background.paper' : 'white',
             color: 'text.primary',
             zIndex: (theme) => theme.zIndex.drawer + 1,
           }}
         >
                     <Toolbar sx={{ 
             display: 'flex', 
             justifyContent: 'space-between',
             px: 2,
             py: 1
           }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!headerOnly && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={open ? handleDrawerClose : handleDrawerOpen}
                  edge="start"
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" noWrap component="div">
                {getPageTitle()}
              </Typography>
            </Box>

            {/* Theme Selector (user menu removed) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="large"
                color="inherit"
                onClick={handleThemeMenuOpen}
                aria-label="theme selector"
              >
                {themeMode === THEME_MODES.LIGHT ? <LightModeIcon /> : 
                 themeMode === THEME_MODES.DARK ? <DarkModeIcon /> : 
                 <SettingsBrightnessIcon />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBarStyled>
      )}
      {headerOnly ? null : <Sidebar open={open} handleDrawerClose={handleDrawerClose} handleDrawerOpen={handleDrawerOpen} />}
      
      {/* Theme Menu */}
      <Menu
        anchorEl={themeMenuAnchor}
        open={Boolean(themeMenuAnchor)}
        onClose={handleThemeMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleThemeChange('system')}>
          <ListItemIcon>
            <SettingsBrightnessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>System</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('light')}>
          <ListItemIcon>
            <LightModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Light</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleThemeChange('dark')}>
          <ListItemIcon>
            <DarkModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dark</ListItemText>
        </MenuItem>
      </Menu>
      
             <Box
         component="main"
         sx={{
           flexGrow: 1,
           p: isThreadsPage ? 0 : { xs: 2, sm: 3 },
           transition: (theme: Theme) =>
             theme.transitions.create('margin', {
               easing: theme.transitions.easing.sharp,
               duration: theme.transitions.duration.leavingScreen,
             }),
           boxSizing: 'border-box',
           backgroundColor: isDarkMode ? 'background.default' : 'background.paper',
           // Threads page: fixed height and no scroll so only inner message area scrolls (avoids double scrollbar)
           ...(isThreadsPage && {
             height: '100vh',
             overflow: 'hidden',
             display: 'flex',
             flexDirection: 'column',
           }),
           ...(!isThreadsPage && {
             minHeight: '100vh',
             overflow: 'auto',
           }),
         }}
       >
        {/* Spacer below fixed elements: non-threads pages get AppBar + optional banner height; threads page gets banner height only when banner is shown (no AppBar on threads). */}
        {!isThreadsPage && (
          <Box sx={{ minHeight: showSubscriptionBanner ? 112 : 64, flexShrink: 0 }} />
        )}
        {isThreadsPage && showSubscriptionBanner && (
          <Box sx={{ minHeight: SUBSCRIPTION_BANNER_HEIGHT, flexShrink: 0 }} />
        )}
        {isThreadsPage ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};

export default Layout; 
